// Complete business/boutique data model for onboarding and management
import { GeoPoint, Timestamp } from 'firebase/firestore';

// ── Onboarding Status ──────────────────────────────────────────────
export type BusinessStatus = 'onboarding' | 'active' | 'suspended' | 'paused';

export const ONBOARDING_STEPS = [
  { key: 'identity', label: 'Business Identity', number: 1 },
  { key: 'address', label: 'Location', number: 2 },
  { key: 'hours', label: 'Hours', number: 3 },
  { key: 'fulfillment', label: 'Fulfillment', number: 4 },
  { key: 'inventory', label: 'Inventory', number: 5 },
  { key: 'legal', label: 'Legal & Payouts', number: 6 },
  { key: 'social', label: 'Social', number: 7 },
  { key: 'review', label: 'Review & Launch', number: 8 },
] as const;

export type OnboardingStepKey = typeof ONBOARDING_STEPS[number]['key'];

// ── Categories ──────────────────────────────────────────────────────
export const BOUTIQUE_CATEGORIES = [
  'Women\'s Fashion',
  'Men\'s Fashion',
  'Streetwear',
  'Luxury',
  'Vintage',
  'Accessories',
  'Shoes',
  'Activewear',
  'Swimwear',
  'Jewelry',
  'Beauty',
  'Kids',
  'Plus Size',
  'Sustainable',
  'Designer',
  'Bridal',
  'Outerwear',
] as const;

// ── Day / Hours ─────────────────────────────────────────────────────
export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export const DAYS_OF_WEEK: DayOfWeek[] = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
];

export interface DayHours {
  open: string; // "09:00"
  close: string; // "18:00"
}

export type WeeklyHours = {
  [key in DayOfWeek]: DayHours | null; // null = closed
};

// ── Store Closures & Schedule Overrides ─────────────────────
export type ClosureReason = 'holiday' | 'event' | 'vacation' | 'maintenance' | 'weather' | 'personal' | 'other';

export interface ScheduledClosure {
  id: string;
  date: string;           // "YYYY-MM-DD"
  endDate?: string;       // "YYYY-MM-DD" for multi-day closures
  reason: ClosureReason;
  label: string;          // e.g. "Memorial Day", "Inventory Day"
  allDay: boolean;
  customOpen?: string;    // if not allDay, reduced hours open
  customClose?: string;   // if not allDay, reduced hours close
  createdAt: Date | Timestamp;
}

export interface StoreHoursConfig {
  hours: WeeklyHours;
  timezone: string;
  closedNow: boolean;           // instant "close for the day" toggle
  closedNowUntil?: string;      // ISO date string — auto-reopens next business day
  scheduledClosures: ScheduledClosure[];
}

// ── Fulfillment ─────────────────────────────────────────────────────
export type FulfillmentMethod = 'fash_drivers' | 'self_delivery' | 'both';

export type PackagingType = 'standard' | 'fragile' | 'garment_bag' | 'gift_wrap';

export type DropoffType = 'DOOR' | 'CURBSIDE' | 'LEAVE_AT_DOOR';

export type UndeliverableAction = 'return' | 'leave_at_door';

export interface FulfillmentConfig {
  method: FulfillmentMethod;
  packaging: PackagingType[];
  maxOrdersPerHour: number | null;
  pickupInstructions: string;
  defaultDropoffType: DropoffType;
  undeliverableAction: UndeliverableAction;
  returnTripsEnabled: boolean;
  requiresGarmentBag: boolean;
  avgItemWeightGrams: number | null;
  requireSignature: boolean;
  requirePhotoProof: boolean;
}

// ── Inventory Settings ──────────────────────────────────────────────
export interface InventorySettings {
  trackInventory: boolean;
  lowStockThreshold: number;
}

// ── Legal Info ──────────────────────────────────────────────────────
export type BusinessType = 'sole_proprietorship' | 'llc' | 'corporation' | 'partnership' | 'nonprofit';

export type ReturnPolicyType = 'full_refund' | 'exchange_only' | 'store_credit' | 'no_returns' | 'custom';

export type StorePolicySource = 'text' | 'url' | 'none';

export interface StorePolicy {
  source: StorePolicySource;
  text: string;
  url: string;
}

export type TaxIdType = 'ein' | 'ssn';

export interface TaxAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface AuthorizedRepresentative {
  fullName: string;
  title: string;
  email: string;
  phone: string;
}

export interface W9Certification {
  certified: boolean;
  certifiedAt: string | null;
  certifiedName: string;
}

export interface LegalInfo {
  businessName: string;
  dbaName: string;
  taxId: string;
  taxIdType: TaxIdType;
  businessType: BusinessType;
  businessAddress: TaxAddress;
  stateOfIncorporation: string;
  dateOfFormation: string;
  authorizedRep: AuthorizedRepresentative;
  w9Certification: W9Certification;
  returnPolicy: {
    type: ReturnPolicyType;
    description: string;
    windowDays: number;
  };
  storePolicy: StorePolicy;
}

/**
 * Placeholder the server sends instead of a stored TIN, and that the client
 * sends back when the merchant did not retype it. Submitting this value
 * leaves the encrypted TIN on file untouched.
 */
export const TAX_ID_SENTINEL = '__unchanged__';

/** Response of the getBusinessTaxInfo callable. `taxId` is never plaintext. */
export interface ClientSafeLegal extends LegalInfo {
  /** e.g. "**-***6789". Display only. */
  taxIdMasked: string;
  taxIdLast4: string;
}

// ── Shopify Connection ─────────────────────────────────────────────
export interface ShopifyConnection {
  connected: boolean;
  shopDomain: string;
  storeName: string;
  accessToken?: string;
  /** IDs of all app webhooks registered on the store (orders, inventory, etc.). */
  webhookIds?: string[];
  /** @deprecated Single-webhook id retained for backward compatibility. */
  webhookId?: string;
  webhookSecret?: string;
  /** OAuth scopes granted at connect time; used to detect when re-consent is needed. */
  scopes?: string;
  syncEnabled: boolean;
  lastSyncAt?: Date;
  connectedAt?: Date;
  uninstalledAt?: Date;
  redactedAt?: Date;
  needsReconsent?: boolean;
  missingScopes?: string[];
}


// ── Stripe Connection ──────────────────────────────────────────────
export type StripeMode = 'test' | 'live';

export interface StripeConnectionStatus {
  connectAccountId: string | null;
  connected: boolean;
  onboardingComplete: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  mode: StripeMode | null;
  accountName: string | null;
  lastFour: string | null;
  connectedAt?: Date;
  /** What Stripe still needs before it will enable charges or payouts. */
  requirements?: {
    currentlyDue: string[];
    pastDue: string[];
    eventuallyDue: string[];
    disabledReason: string | null;
    currentDeadline: number | null;
  };
}

// ── Payout Compliance ───────────────────────────────────────────────
// FASH withholds a store's proceeds until its tax section (W-9
// equivalent) is complete and Stripe has enabled payouts. The verdict is
// computed server-side; the client only ever reads it.

export type TaxRequirementCode =
  | 'legal_business_name'
  | 'business_type'
  | 'tax_id_type'
  | 'tax_id'
  | 'tax_address'
  | 'state_of_incorporation'
  | 'date_of_formation'
  | 'authorized_rep'
  | 'w9_certification';

export interface MissingTaxRequirement {
  code: TaxRequirementCode;
  label: string;
}

export type TaxComplianceStatus = 'complete' | 'incomplete' | 'needs_recertification';

/** Server-maintained cache on the public business document. */
export interface TaxComplianceBlock {
  status: TaxComplianceStatus;
  taxComplete: boolean;
  stripeReady: boolean;
  payoutEligible: boolean;
  missing: MissingTaxRequirement[];
  stripeBlockers: string[];
  /** Non-compliant but still inside the warning window; nothing is withheld. */
  inGracePeriod?: boolean;
  /** Money is actually being withheld. */
  holdPayouts?: boolean;
  evaluatedAt?: Timestamp | Date;
  firstIncompleteAt?: Timestamp | Date;
  graceEndsAt?: Timestamp | Date;
  completedAt?: Timestamp | Date;
  lastNoticeAt?: Timestamp | Date;
}

/** Response of the getPayoutComplianceStatus callable. */
export interface PayoutComplianceStatus {
  businessId: string;
  taxComplete: boolean;
  stripeReady: boolean;
  payoutEligible: boolean;
  status: TaxComplianceStatus;
  missing: MissingTaxRequirement[];
  stripeBlockers: string[];
  inGracePeriod: boolean;
  /** Epoch millis, or null if the store has never been evaluated. */
  graceEndsAt: number | null;
  holdPayouts: boolean;
  held: {
    count: number;
    totalCents: number;
    oldestHeldAt: number | null;
  };
}

// ── Social Media ────────────────────────────────────────────────────
export interface SocialMedia {
  instagram: string;
  tiktok: string;
  website: string;
}

// ── Address ─────────────────────────────────────────────────────────
export interface BusinessAddress {
  street: string;
  unit: string;
  city: string;
  state: string;
  zipCode: string;
  // Captured from Places Autocomplete when available so the store pickup
  // location + handoff map have exact coordinates without re-geocoding.
  lat?: number;
  lng?: number;
}

// ── Seats (employee accounts attached to a store) ───────────────────
// A seat is a non-owner employee login. Seats never see legal, tax,
// payout, or store-wide financial data — those live in the owner-only
// businesses/{id}/private subcollection.

export type SeatRole = 'manager' | 'staff';

export type SeatStatus = 'invited' | 'active' | 'suspended';

export type SeatPermission =
  | 'orders'
  | 'products'
  | 'hours'
  | 'ownSales';

export const SEAT_PERMISSIONS: Record<SeatRole, readonly SeatPermission[]> = {
  manager: ['orders', 'products', 'hours', 'ownSales'],
  staff: ['orders', 'hours', 'ownSales'],
};

export const SEAT_ROLE_LABELS: Record<SeatRole, { label: string; description: string }> = {
  manager: {
    label: 'Manager',
    description: 'Orders, products and inventory, store hours, and their own sales',
  },
  staff: {
    label: 'Staff',
    description: 'Orders, store hours, and their own sales',
  },
};

export function seatCan(seatRole: SeatRole | null, permission: SeatPermission): boolean {
  if (!seatRole) return false;
  return SEAT_PERMISSIONS[seatRole].includes(permission);
}

/** businesses/{businessId}/seats/{seatUid} */
export interface SeatDocument {
  uid: string;
  email: string;
  displayName: string;
  seatRole: SeatRole;
  status: SeatStatus;
  invitedBy: string;
  createdAt: Timestamp | Date;
  updatedAt?: Timestamp | Date;
  lastActiveAt?: Timestamp | Date | null;
}

/** business_invites/{token} — Cloud Functions only, never client-readable */
export interface SeatInvite {
  token: string;
  businessId: string;
  businessName: string;
  email: string;
  displayName: string;
  seatRole: SeatRole;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  invitedBy: string;
  createdAt: Timestamp | Date;
  expiresAt: Timestamp | Date;
  acceptedAt?: Timestamp | Date;
}

/** businesses/{businessId}/join_requests/{uid} */
export interface SeatJoinRequest {
  uid: string;
  email: string;
  displayName: string;
  status: 'pending' | 'approved' | 'denied';
  createdAt: Timestamp | Date;
  resolvedAt?: Timestamp | Date;
  resolvedBy?: string;
}

// ── Full Business Document (Firestore) ──────────────────────────────
export interface BusinessDocument {
  // Identity (Step 1)
  name: string;
  description: string;
  categories: string[];
  logo: string;
  coverImage: string;
  /** Dedicated homepage hero image when the store is featured. Falls back to coverImage. */
  featuredImage?: string;
  /** Lower numbers appear first in the homepage featured carousel. Admin-managed. */
  featuredOrder?: number;

  // Address & Location (Step 2)
  address: BusinessAddress;
  phone: string;
  formattedAddress: string; // human-readable composite
  location: GeoPoint | null;
  deliveryRadius: number; // miles

  // Hours (Step 3)
  hours: WeeklyHours;
  timezone: string;
  prepTimeEstimate: number; // minutes

  // Fulfillment (Step 4)
  fulfillment: FulfillmentConfig;

  // Inventory (Step 5)
  inventorySettings: InventorySettings;
  priceRange: { min: number; max: number };
  collections: string[];

  /**
   * @deprecated Lives in businesses/{id}/private/legal. Still declared so
   * pre-migration documents type-check; read through usePrivateBusinessData.
   */
  legal?: LegalInfo;

  // Social (Step 7)
  socialMedia: SocialMedia;
  brandColor: string;

  /** @deprecated Lives in businesses/{id}/private/legal alongside `legal`. */
  stripe?: StripeConnectionStatus;

  // Shopify (set via Cloud Function)
  shopify?: ShopifyConnection;

  /**
   * Payout eligibility verdict. Written only by Cloud Functions —
   * Firestore rules reject client writes so it cannot be spoofed.
   */
  taxCompliance?: TaxComplianceBlock;

  // System fields
  ownerId: string;
  status: BusinessStatus;
  onboardingStep: number;
  active: boolean;
  featured: boolean;
  rating: number;
  reviewCount: number;
  /** Short code employees enter to request a seat. Set by Cloud Function. */
  joinCode?: string;
  seatCount?: number;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

/**
 * businesses/{businessId}/private/legal — owner and platform admin only.
 * Holds everything a seat must never see.
 */
export interface PrivateBusinessData {
  legal: LegalInfo;
  stripe?: StripeConnectionStatus;
  updatedAt?: Timestamp | Date;
}

// ── Onboarding Form State (local, before saving) ───────────────────
export interface OnboardingFormData {
  // Step 1
  name: string;
  description: string;
  categories: string[];
  logoFile: File | null;
  logoPreview: string;
  coverFile: File | null;
  coverPreview: string;

  // Step 2
  address: BusinessAddress;
  phone: string;
  deliveryRadius: number;

  // Step 3
  hours: WeeklyHours;
  timezone: string;
  prepTimeEstimate: number;

  // Step 4
  fulfillment: FulfillmentConfig;

  // Step 5
  inventorySettings: InventorySettings;
  priceRange: { min: number; max: number };
  collections: string[];

  // Step 6
  legal: LegalInfo;

  // Step 7
  socialMedia: SocialMedia;
  brandColor: string;
}

// ── Default form values ─────────────────────────────────────────────
export const DEFAULT_WEEKLY_HOURS: WeeklyHours = {
  monday: { open: '10:00', close: '18:00' },
  tuesday: { open: '10:00', close: '18:00' },
  wednesday: { open: '10:00', close: '18:00' },
  thursday: { open: '10:00', close: '18:00' },
  friday: { open: '10:00', close: '18:00' },
  saturday: { open: '11:00', close: '17:00' },
  sunday: null,
};

export const DEFAULT_FORM_DATA: OnboardingFormData = {
  // Step 1
  name: '',
  description: '',
  categories: [],
  logoFile: null,
  logoPreview: '',
  coverFile: null,
  coverPreview: '',

  // Step 2
  address: { street: '', unit: '', city: '', state: '', zipCode: '' },
  phone: '',
  deliveryRadius: 5,

  // Step 3
  hours: DEFAULT_WEEKLY_HOURS,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  prepTimeEstimate: 30,

  // Step 4
  fulfillment: {
    method: 'fash_drivers',
    packaging: ['standard'],
    maxOrdersPerHour: null,
    pickupInstructions: '',
    defaultDropoffType: 'DOOR',
    undeliverableAction: 'return',
    returnTripsEnabled: true,
    requiresGarmentBag: false,
    avgItemWeightGrams: null,
    requireSignature: false,
    requirePhotoProof: false,
  },

  // Step 5
  inventorySettings: { trackInventory: true, lowStockThreshold: 5 },
  priceRange: { min: 0, max: 500 },
  collections: [],

  // Step 6
  legal: {
    businessName: '',
    dbaName: '',
    taxId: '',
    taxIdType: 'ein',
    businessType: 'sole_proprietorship',
    businessAddress: { street: '', city: '', state: '', zipCode: '' },
    stateOfIncorporation: '',
    dateOfFormation: '',
    authorizedRep: { fullName: '', title: '', email: '', phone: '' },
    w9Certification: { certified: false, certifiedAt: null, certifiedName: '' },
    returnPolicy: { type: 'full_refund', description: '', windowDays: 30 },
    storePolicy: { source: 'none', text: '', url: '' },
  },

  // Step 7
  socialMedia: { instagram: '', tiktok: '', website: '' },
  brandColor: '#C084FC',
};

// ── US States for dropdown ──────────────────────────────────────────
export const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
] as const;
